import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { AppState } from '../../../../reducers';
import {
  addNewSearch,
  clearSelectedCompanies,
  clearState,
  getPageDataForDrillDown,
  loadCompanies,
  removeNonStudyComparables,
  resetSearch,
  saveSelectedCompanies,
  selectCompanyDrillDownTab,
  sendExternalDataForDrillDown,
  sendNavigationDataForDrillDown,
  sendSelectedComparableIdForDrillDown,
  updateCurrentPage,
  updateHash,
  updateNextPage,
  updateSearchInput,
} from './company-search.actions';
import {
  isNextPageLoaded,
  selectCompanies,
  selectComparableIdsByPageNumber,
  selectSearchData,
  selectSelectedCompaniesCount,
  selectSelectedCompaniesMap,
} from './company-search.selectors';
import objectHash from 'object-hash';
import { takeUntil } from 'rxjs/operators';
import { Paginator } from 'primeng/paginator';
import { selectStudyDatabaseNames } from '../../../study.selectors';
import { DictionaryPage } from '../company-drill-down/models/dictionary-page.model';
import { SearchCompany } from './models/search-company.model';
import { selectDrillDownPageDataRequestIndication } from '../company-drill-down/drill-down.selectors';

@Component({
  selector: 'app-company-slide-out-search',
  templateUrl: './company-slide-out-search.component.html',
  styleUrls: ['./company-slide-out-search.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class CompanySlideOutSearchComponent implements OnInit, OnDestroy {
  @ViewChild('paginator', { static: false }) paginator: Paginator;

  searchInput: string = null;
  selectedCompanies: SearchCompany[] = [];
  searchData: {
    totalCount: number;
    itemsPerPage: number;
    currentPage: number;
    currentSearchHash: string;
    currentSearchInput: string;
    isNextPageSuccess: boolean;
    isNextPageFailed: boolean;
  };
  selectedCompaniesMap: { [page: number]: any[] };
  studyDatabaseNamesMap: { [id: number]: string };
  comparableIdsByPageNumber: DictionaryPage;

  companies$: Observable<SearchCompany[]>;
  selectedCompaniesCount$: Observable<number>;

  destroy$ = new Subject();

  constructor(private store: Store<AppState>) {}

  ngOnInit(): void {
    this.companies$ = this.store.pipe(select(selectCompanies));
    this.selectedCompaniesCount$ = this.store.pipe(select(selectSelectedCompaniesCount));

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectDrillDownPageDataRequestIndication))
      .subscribe((data) => {
        if (!data || !data.isPageDataRequested) return;

        this.store.dispatch(getPageDataForDrillDown({ page: data.page, index: data.index }));
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchData))
      .subscribe((data) => {
        this.searchData = data;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSelectedCompaniesMap))
      .subscribe((selectedCompanies) => {
        this.selectedCompaniesMap = selectedCompanies;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(isNextPageLoaded))
      .subscribe((isSuccess) => {
        if (isSuccess) {
          this.selectedCompanies = this.selectedCompaniesMap[this.searchData.currentPage] || [];
        }
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectStudyDatabaseNames))
      .subscribe((namesMap) => {
        this.studyDatabaseNamesMap = namesMap;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectComparableIdsByPageNumber))
      .subscribe((pages) => {
        this.comparableIdsByPageNumber = pages;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
    this.store.dispatch(clearState());
    this.store.dispatch(removeNonStudyComparables());
  }

  reset() {
    this.searchInput = null;
    this.clearSelected();
    this.store.dispatch(resetSearch());
  }

  search() {
    const input = this.searchInput
      .split(';')
      .filter((i) => i)
      .map((i) => i.trim())
      .join(';');
    const hash = objectHash.sha1(input);

    this.store.dispatch(updateHash({ hash }));
    this.store.dispatch(updateSearchInput({ searchInput: this.searchInput }));

    this.clearSelected();

    this.paginate(0);
  }

  paginate(event) {
    if (!this.searchData.currentSearchInput) {
      return;
    }

    this.store.dispatch(updateNextPage({ page: event.page }));

    if (this.searchData.isNextPageSuccess) {
      this.store.dispatch(updateCurrentPage({ page: event.page }));
    } else {
      if (!this.searchData.isNextPageFailed) {
        this.store.dispatch(addNewSearch({ hash: this.searchData.currentSearchHash, page: event.page }));
      }
      this.store.dispatch(
        loadCompanies({
          hash: this.searchData.currentSearchHash,
          page: event.page,
          itemsPerPage: this.searchData.itemsPerPage,
          searchInput: this.searchData.currentSearchInput,
        })
      );
    }
  }

  handleChange() {
    this.store.dispatch(
      saveSelectedCompanies({
        page: this.searchData.currentPage,
        ids: this.selectedCompanies.map((c) => {
          return { comparableId: c.comparableId };
        }),
      })
    );
  }

  clearSelected() {
    this.selectedCompanies = [];
    this.store.dispatch(clearSelectedCompanies());
    this.resetPagination();
  }

  getDbNames(databaseIds: number[]) {
    const databaseNames = databaseIds.map((id) => this.studyDatabaseNamesMap[id]);
    return databaseNames.join(', ');
  }

  selectCompany(comparableId: number, index: number) {
    this.store.dispatch(selectCompanyDrillDownTab());

    this.store.dispatch(sendSelectedComparableIdForDrillDown({ id: comparableId }));
    this.store.dispatch(sendNavigationDataForDrillDown({ page: this.searchData.currentPage, index }));
    this.store.dispatch(
      sendExternalDataForDrillDown({
        idsByPage: this.comparableIdsByPageNumber,
        count: this.searchData.totalCount,
        rows: this.searchData.itemsPerPage,
      })
    );
  }

  private resetPagination() {
    // Hack to get the paginator to reset to page 1
    // const $event = { preventDefault: () => null };
    if (this.paginator) {
      this.paginator.changePage(0);
    }
  }
}
